import {
  AdobeTargetAllMBoxValues,
  ExperienceGroup,
  GetPrefetchOffersResponse
} from 'components/app/AdobeTargetProvider/types';
import {ABTestMboxes, ABTestMboxesArray} from 'enums/ABTests';
import {ensighten} from 'shared/Ensighten/components';
import {useAdobeTarget} from 'utils/adobe-target';

/**
 * AB Tests must be retrieved and cached as "prefetch" requests. This allows them to be retrieved at any arbitrary time
 * before the test may begin, but won't consider the user to be part of the test audience until sendNotifications() is called
 * https://experienceleague.adobe.com/en/docs/target-dev/developer/client-side/at-js-implementation/functions-overview/adobe-target-getoffers-atjs-2
 **/

export const getPrefetchABTests = (adobe: any): Promise<GetPrefetchOffersResponse> => {
  const request = {
    request: {
      prefetch: {
        mboxes: ABTestMboxesArray.map((mbox, index) => ({name: mbox, index: index}))
      }
    }
  };

  return adobe.target.getOffers(request);
};

export const getPrefetchMBox = (prefetch: GetPrefetchOffersResponse, mboxName: string) => {
  const mboxes = prefetch.prefetch.mboxes;
  return mboxes.find((mbox) => mbox.name === mboxName);
};

export const getABTestExperience = ({
  prefetch,
  mboxName,
  triggerExperienceView = true
}: {
  prefetch: GetPrefetchOffersResponse;
  mboxName: string;
  triggerExperienceView?: boolean;
}) => {
  const mbox = getPrefetchMBox(prefetch, mboxName);
  if (mbox?.options?.length) {
    if (triggerExperienceView) {
      triggerExperienceViewedEffects({mbox});
    }
    return mbox.options[0]?.content.experienceGroup;
  }
  return undefined;
};

const checkExperience = ({
  target,
  mboxName,
  experienceGroup,
  triggerExperienceView = true
}: {
  mboxName: ABTestMboxes;
  experienceGroup: ExperienceGroup;
  target?: AdobeTargetAllMBoxValues;
  triggerExperienceView?: boolean;
}) => {
  if (!target) {
    return null;
  }
  const experience = getABTestExperience({prefetch: target.abPrefetch, mboxName, triggerExperienceView});
  if (experience === undefined) {
    return null;
  }
  return experience === experienceGroup;
};

export const isAExperience = (params: Omit<Parameters<typeof checkExperience>[0], 'experienceGroup'>) => {
  const experienceGroup = checkExperience({...params, experienceGroup: 'A'});
  //default to A experience if no experience group is found, A experiences should be the control group
  if (experienceGroup === null) {
    return true;
  }
  return experienceGroup;
};

export const isBExperience = (params: Omit<Parameters<typeof checkExperience>[0], 'experienceGroup'>) =>
  !!checkExperience({...params, experienceGroup: 'B'});

const triggerExperienceViewedEffects = ({mbox}: {mbox: NonNullable<ReturnType<typeof getPrefetchMBox>>}) => {
  if (!shouldSendNotificationsAndSetStatusInStorage(mbox)) {
    return;
  }
  sendNotifications(mbox);
  ensighten.setABTestData(mbox);
};

/**
 * This triggers the API call that tells target that the user has officially been shown the A or B experience. Prefetch
 * only retrieves the experiences and we do this right up front so that data can be cached-- we don't want the customer to
 * be included in a metric unless they actually see the experience, which sendNotifications is responsible for
 * https://experienceleague.adobe.com/en/docs/target-dev/developer/client-side/at-js-implementation/functions-overview/adobe-target-sendnotifications-atjs-21
 */
const sendNotifications = (mbox: NonNullable<ReturnType<typeof getPrefetchMBox>>) => {
  const {adobe} = window as any;

  const request = {request: {notifications: [createNotificationRequest(mbox)]}};
  adobe.target.sendNotifications(request);
};

export const trackClick = (params?: {targetConversionMBox?: ABTestMboxes}) => {
  if (params?.targetConversionMBox) {
    trackEvent(params.targetConversionMBox);
  }
};

/**
 * This triggers the API call for notifying Target that the user has clicked on an mbox.
 * Note: When configuring the conversion metric in target, you should actually configure it as "Viewed an mbox"
 * "Clicked an mbox" does not function, I have tried without any success to make it actually work and it
 * turns out adobe's official community response is that it's a legacy feature for the old version of the platform.
 * This, of course, doesn't actually appear anywhere in the documentation 👍
 * https://experienceleaguecommunities.adobe.com/t5/adobe-target-questions/quot-clicked-an-mbox-quot-tracks-no-conversions-but-quot-viewed/m-p/384103#M68
 */
const trackEvent = (mbox: ABTestMboxes) => {
  const {adobe} = window as any;
  adobe.target.trackEvent({
    mbox
  });
};

const createNotificationRequest = (mbox: GetPrefetchOffersResponse['prefetch']['mboxes'][0]) => {
  const id = `${Math.random().toString().substring(2)}-${Math.random().toString().substring(2)}-${Math.random().toString().substring(2)}`;
  const timestamp = Date.now();

  return {
    id,
    type: 'display',
    timestamp,
    mbox: {name: mbox.name, state: mbox.state},
    tokens: mbox.options?.map((option) => option.eventToken)
  };
};

type TargetNotificationsSent = {
  [key: string]: boolean;
};
const TARGET_NOTIFICATIONS_SENT_STORAGE_KEY = 'targetNotificationsSent';

const shouldSendNotificationsAndSetStatusInStorage = (mbox: NonNullable<ReturnType<typeof getPrefetchMBox>>) => {
  const targetNotificationsSent = JSON.parse(
    sessionStorage.getItem(TARGET_NOTIFICATIONS_SENT_STORAGE_KEY) || '{}'
  ) as TargetNotificationsSent;
  const value = targetNotificationsSent[mbox.name];

  if (!value) {
    targetNotificationsSent[mbox.name] = true;
    sessionStorage.setItem(TARGET_NOTIFICATIONS_SENT_STORAGE_KEY, JSON.stringify(targetNotificationsSent));
    return true;
  }
  return false;
};

export const useGenerateNameForDataID = ({name, abTestMBox}: {name: string; abTestMBox?: ABTestMboxes}) => {
  const target = useAdobeTarget();
  if (!abTestMBox) {
    return name;
  }
  const experience = getABTestExperience({
    prefetch: target.abPrefetch,
    mboxName: abTestMBox,
    triggerExperienceView: false
  });
  return experience ? `${name}.abtest--${abTestMBox}-${experience}` : name;
};
